import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Search, Filter, Check, Info, Star } from 'lucide-react';
import { SAMPLE_SUBSTRATE_PROFILES } from '../data/substrateProfiles';
import type { SubstrateProfile } from '../types/substrate';

interface SubstrateSelectorProps {
  selectedSubstrate?: SubstrateProfile;
  onSelect: (substrate: SubstrateProfile) => void;
  className?: string;
}

export function SubstrateSelector({ 
  selectedSubstrate, 
  onSelect, 
  className = '' 
}: SubstrateSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const categories = useMemo(() => {
    const cats = new Set(SAMPLE_SUBSTRATE_PROFILES.map(s => s.category));
    return ['all', ...Array.from(cats)];
  }, []);

  const filteredSubstrates = useMemo(() => {
    let filtered = SAMPLE_SUBSTRATE_PROFILES;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(s => s.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(query) ||
        (s.manufacturer && s.manufacturer.toLowerCase().includes(query)) ||
        s.category.toLowerCase().includes(query) ||
        s.metadata?.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [searchQuery, selectedCategory]);

  const handleSubstrateSelect = useCallback((substrate: SubstrateProfile) => {
    onSelect(substrate);
  }, [onSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const items = filteredSubstrates;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => {
          const next = prev + 1;
          return next < items.length ? next : prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => {
          const next = prev - 1;
          return next >= 0 ? next : 0;
        });
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(items.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < items.length) {
          handleSubstrateSelect(items[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        searchInputRef.current?.focus();
        setFocusedIndex(-1);
        break;
    }
  }, [filteredSubstrates, focusedIndex, handleSubstrateSelect]);

  const getCategoryColor = (category: string) => {
    const colors = {
      paper: 'bg-blue-100 text-blue-800 border-blue-200',
      metallic: 'bg-gray-100 text-gray-800 border-gray-200',
      plastic: 'bg-green-100 text-green-800 border-green-200',
      fabric: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Announce results to screen readers
  const announceResults = useCallback(() => {
    const count = filteredSubstrates.length;
    const announcement = count === 0 
      ? 'No substrates found matching your criteria'
      : `${count} substrate${count !== 1 ? 's' : ''} found`;
    
    // Create live region announcement
    const announcementElement = document.createElement('div');
    announcementElement.setAttribute('aria-live', 'polite');
    announcementElement.setAttribute('aria-atomic', 'true');
    announcementElement.className = 'sr-only';
    announcementElement.textContent = announcement;
    document.body.appendChild(announcementElement);
    
    setTimeout(() => {
      document.body.removeChild(announcementElement);
    }, 1000);
  }, [filteredSubstrates.length]);

  useEffect(() => {
    announceResults();
  }, [announceResults]);

  // Focus management for keyboard navigation
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.querySelector(`[data-index="${focusedIndex}"]`) as HTMLElement;
      focusedElement?.focus();
    }
  }, [focusedIndex]);

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Substrate</h3>
        
        {/* Search and Category Controls */}
        <div className="space-y-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" aria-hidden="true" />
            <label htmlFor="substrate-search" className="sr-only">
              Search substrates by name, manufacturer, or description
            </label>
            <input
              id="substrate-search"
              ref={searchInputRef}
              type="text"
              placeholder="Search substrates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              aria-describedby="search-results-count"
            />
          </div>

          {/* Category Navigation */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" aria-hidden="true" />
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by substrate category">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    selectedCategory === category
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  aria-pressed={selectedCategory === category}
                  aria-describedby="category-description"
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>
            <div id="category-description" className="sr-only">
              Filter substrates by material category
            </div>
          </div>
        </div>
      </div>

      {/* Substrate List */}
      <div className="max-h-96 overflow-y-auto" ref={listRef} role="region" aria-label="Substrate options">
        {filteredSubstrates.length === 0 ? (
          <div className="p-8 text-center text-gray-500" role="status" aria-live="polite">
            <Info className="w-8 h-8 mx-auto mb-2 text-gray-400" aria-hidden="true" />
            <p>No substrates found matching your criteria.</p>
          </div>
        ) : (
          <div className="p-2 space-y-2" role="listbox" aria-label="Available substrates">
            {filteredSubstrates.map((substrate, index) => (
              <button
                key={substrate.id}
                data-index={index}
                onClick={() => handleSubstrateSelect(substrate)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSubstrateSelect(substrate);
                  }
                }}
                className={`w-full p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-left ${
                  selectedSubstrate?.id === substrate.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                role="option"
                aria-selected={selectedSubstrate?.id === substrate.id}
                aria-describedby={`substrate-${substrate.id}-details`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Header Row */}
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 truncate">
                        {substrate.name}
                      </h4>
                      {selectedSubstrate?.id === substrate.id && (
                        <Check className="w-5 h-5 text-blue-500 flex-shrink-0" aria-hidden="true" />
                      )}
                    </div>

                    {/* Manufacturer and Category */}
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm text-gray-600">
                        {substrate.manufacturer}
                      </span>
                      <span 
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(substrate.category)}`}
                        aria-label={`Category: ${substrate.category}`}
                      >
                        {substrate.category}
                      </span>
                    </div>

                    {/* Properties */}
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-2">
                      <div className="flex justify-between">
                        <span>Brightness:</span>
                        <span className="font-medium" aria-label={`Brightness: ${substrate.brightness} percent`}>
                          {substrate.brightness}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Glossiness:</span>
                        <span className="font-medium" aria-label={`Glossiness: ${substrate.glossiness} percent`}>
                          {substrate.glossiness}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>TAC Limit:</span>
                        <span className="font-medium" aria-label={`Total area coverage limit: ${substrate.tacLimit} percent`}>
                          {substrate.tacLimit}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Version:</span>
                        <span className="font-medium">{substrate.version}</span>
                      </div>
                    </div>

                    {/* Base Color Preview */}
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Base Color:</span>
                      <div className="flex items-center space-x-1">
                        <div 
                          className="w-4 h-4 rounded border border-gray-300"
                          style={{ backgroundColor: substrate.baseColor.hex }}
                          aria-hidden="true"
                        />
                        <span className="text-xs font-mono text-gray-600" aria-label={`Base color: ${substrate.baseColor.hex}`}>
                          {substrate.baseColor.hex}
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    {substrate.metadata?.description && (
                      <p className="text-xs text-gray-500 mt-2 line-clamp-2" id={`substrate-${substrate.id}-details`}>
                        {substrate.metadata.description}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span id="search-results-count" aria-live="polite">
            {filteredSubstrates.length} substrates found
          </span>
          <div className="flex items-center space-x-1">
            <Star className="w-3 h-3" aria-hidden="true" />
            <span>Industry-standard profiles</span>
          </div>
        </div>
      </div>
    </div>
  );
}
